import { useEffect, useState, useCallback, useMemo } from "react";
import { ShoppingBag, IndianRupee, Package, Truck, AlertTriangle, TrendingUp, Eye, ArrowUpRight, ArrowDownRight, Users, Percent, Repeat, ShoppingCart, MapPin, Activity } from "lucide-react";
import api from "../../lib/api";
import { formatPrice } from "../../lib/format";

const RANGES = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "year", label: "Last year" },
];

const STATUS_TONE = {
  paid: "text-gold border-gold/30",
  confirmed: "text-gold border-gold/30",
  processing: "text-blue-300 border-blue-400/30",
  packed: "text-cyan-300 border-cyan-400/30",
  shipped: "text-cyan-300 border-cyan-400/30",
  out_for_delivery: "text-emerald-300 border-emerald-400/30",
  delivered: "text-emerald-400 border-emerald-400/30",
  cancelled: "text-red-400 border-red-400/30",
  refunded: "text-amber-400 border-amber-400/30",
  returned: "text-amber-400 border-amber-400/30",
};

function DeltaPill({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-[10px] uppercase tracking-[0.2em] text-muted/60">—</span>;
  }
  const up = value >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-0.5 ${up ? "text-emerald-400" : "text-red-400"}`}>
      <Icon size={11} strokeWidth={2} />{Math.abs(value)}%
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, delta, sub, testId }) {
  return (
    <div className="bg-[#141414] p-5 border border-[rgba(244,244,240,0.05)] hover:border-gold/30 transition-colors group" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-8 h-8 rounded-full bg-[#1a1a1a] flex items-center justify-center group-hover:bg-gold/10 transition-colors">
          <Icon size={14} strokeWidth={1.5} className="text-gold" />
        </div>
        {delta !== undefined && <DeltaPill value={delta} />}
      </div>
      <p className="text-[10px] uppercase tracking-[0.25em] text-muted mb-1">{label}</p>
      <p className="font-serif text-3xl text-text leading-none">{value}</p>
      {sub && <p className="text-xs text-muted mt-2">{sub}</p>}
    </div>
  );
}

function SalesChart({ series }) {
  // SVG sparkline-style area chart, normalized
  const { width, height, padX, padY } = { width: 800, height: 220, padX: 24, padY: 16 };
  const maxRevenue = Math.max(1, ...series.map(s => s.revenue));
  const pts = series.map((s, i) => {
    const x = padX + (i / Math.max(1, series.length - 1)) * (width - padX * 2);
    const y = height - padY - (s.revenue / maxRevenue) * (height - padY * 2);
    return [x, y, s];
  });
  const linePath = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${pts[pts.length-1][0].toFixed(1)},${height - padY} L${pts[0][0].toFixed(1)},${height - padY} Z`;

  const first = series[0]?.date;
  const last = series[series.length - 1]?.date;
  const mid = series[Math.floor(series.length / 2)]?.date;

  return (
    <div className="bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)]" data-testid="sales-chart">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">Total sales</p>
          <p className="font-serif text-3xl">{formatPrice(series.reduce((a, s) => a + s.revenue, 0))}</p>
        </div>
        <div className="text-right text-[10px] uppercase tracking-[0.18em] text-muted">
          <p>Peak day · {formatPrice(maxRevenue)}</p>
          <p className="mt-1">{series.reduce((a, s) => a + s.orders, 0)} orders in period</p>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-[200px]" preserveAspectRatio="none">
        <defs>
          <linearGradient id="goldFade" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#C8A97E" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#C8A97E" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Grid */}
        {[0.25, 0.5, 0.75].map(g => (
          <line key={g} x1={padX} x2={width-padX} y1={padY + g*(height-padY*2)} y2={padY + g*(height-padY*2)} stroke="rgba(244,244,240,0.04)" strokeDasharray="2 4" />
        ))}
        <path d={areaPath} fill="url(#goldFade)" />
        <path d={linePath} fill="none" stroke="#C8A97E" strokeWidth="1.5" />
        {pts.map(([x, y, s]) => (
          <circle key={s.date} cx={x} cy={y} r="2" fill="#C8A97E" opacity={s.revenue > 0 ? 1 : 0}>
            <title>{`${s.date}: ${formatPrice(s.revenue)} · ${s.orders} orders`}</title>
          </circle>
        ))}
      </svg>
      <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-muted mt-2 px-6">
        <span>{first}</span><span>{mid}</span><span>{last}</span>
      </div>
    </div>
  );
}

function FulfillmentTile({ icon: Icon, label, count, tone = "text-gold", testId }) {
  return (
    <div className="bg-[#141414] p-4 border border-[rgba(244,244,240,0.05)] flex items-center gap-4" data-testid={testId}>
      <Icon size={20} strokeWidth={1.5} className={tone} />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.22em] text-muted truncate">{label}</p>
        <p className="font-serif text-2xl leading-none mt-1">{count}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard({ onOpenOrder }) {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const [analytics, evts] = await Promise.all([
        api.get(`/admin/analytics?range=${r}`),
        api.get(`/admin/events/summary?hours=${r === "today" ? 24 : r === "7d" ? 168 : 720}`).catch(() => ({ data: null })),
      ]);
      setData(analytics.data);
      setEvents(evts.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(range); }, [load, range]);

  const k = data?.kpis;
  const periodLabel = useMemo(() => RANGES.find(r => r.id === range)?.label || "", [range]);

  if (loading || !data) {
    return <p className="text-muted text-xs uppercase tracking-[0.2em] py-12">Loading dashboard…</p>;
  }

  return (
    <div className="space-y-10" data-testid="admin-dashboard-shopify">
      {/* ────────── Sticky top bar ────────── */}
      <div className="flex items-center justify-between flex-wrap gap-4 pb-5 border-b border-[rgba(244,244,240,0.05)]">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1">Atelier Console</p>
          <h2 className="font-serif text-4xl">Good {greeting()}</h2>
          <p className="text-xs text-muted mt-1.5">Here&apos;s what&apos;s happening with your store · {periodLabel}</p>
        </div>
        <div className="flex items-center gap-2 bg-[#141414] p-1 border border-[rgba(244,244,240,0.06)]">
          {RANGES.map(r => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.22em] transition-colors ${range === r.id ? "bg-gold text-black" : "text-muted hover:text-gold"}`}
              data-testid={`range-${r.id}`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      {/* ────────── KPI grid (6 cards, Shopify-style) ────────── */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard icon={IndianRupee} label="Total sales" value={formatPrice(k.total_sales)} delta={k.total_sales_delta_pct} testId="kpi-total-sales" />
        <KpiCard icon={ShoppingBag} label="Orders" value={k.orders} delta={k.orders_delta_pct} testId="kpi-orders" />
        <KpiCard icon={ShoppingCart} label="Average order value" value={formatPrice(k.aov)} testId="kpi-aov" />
        <KpiCard icon={Users} label="Customers" value={k.customers} sub="Unique buyers" testId="kpi-customers" />
        <KpiCard icon={Repeat} label="Returning rate" value={`${k.returning_customer_rate}%`} testId="kpi-returning" />
        <KpiCard icon={Percent} label="Discounts given" value={formatPrice(k.discount_given)} testId="kpi-discounts" />
      </div>

      {/* ────────── Sales chart ────────── */}
      <SalesChart series={data.sales_series} />

      {/* ────────── Fulfillment + Inventory ────────── */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Fulfillment</p>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted">Action required</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <FulfillmentTile icon={Package} label="To fulfill" count={data.fulfillment.to_fulfill} testId="ful-to-fulfill" />
          <FulfillmentTile icon={Package} label="To pack" count={data.fulfillment.to_pack} tone="text-blue-300" testId="ful-to-pack" />
          <FulfillmentTile icon={Truck} label="To ship" count={data.fulfillment.to_ship} tone="text-cyan-300" testId="ful-to-ship" />
          <FulfillmentTile icon={Truck} label="In transit" count={data.fulfillment.in_transit} tone="text-emerald-300" testId="ful-in-transit" />
          <FulfillmentTile icon={AlertTriangle} label="Low stock" count={data.inventory.low_stock} tone="text-amber-400" testId="ful-low-stock" />
          <FulfillmentTile icon={AlertTriangle} label="Out of stock" count={data.inventory.out_of_stock} tone="text-red-400" testId="ful-oos" />
        </div>
      </section>

      {/* ────────── Top products + Top cities ────────── */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)]">
          <div className="flex items-center justify-between mb-5">
            <p className="text-xs uppercase tracking-[0.25em] text-gold flex items-center gap-2"><TrendingUp size={12} strokeWidth={1.5} /> Top products</p>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted">By revenue</span>
          </div>
          {data.top_products.length === 0 ? (
            <p className="text-muted text-sm">No sales in this period yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.top_products.map((p, idx) => (
                <li key={p.product_id} className="flex items-center gap-4" data-testid={`top-product-${idx}`}>
                  <span className="text-[10px] text-muted w-4 text-right tabular-nums">{idx + 1}</span>
                  {p.image ? <img src={p.image} alt="" className="w-10 h-12 object-cover" /> : <div className="w-10 h-12 bg-[#1a1a1a]" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-sm truncate">{p.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted mt-0.5">{p.quantity} sold</p>
                  </div>
                  <p className="font-serif text-sm text-gold whitespace-nowrap">{formatPrice(p.revenue)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)]">
          <div className="flex items-center justify-between mb-5">
            <p className="text-xs uppercase tracking-[0.25em] text-gold flex items-center gap-2"><MapPin size={12} strokeWidth={1.5} /> Top cities</p>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted">By orders</span>
          </div>
          {data.top_cities.length === 0 ? (
            <p className="text-muted text-sm">No deliveries yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.top_cities.map((c, idx) => {
                const maxOrders = Math.max(...data.top_cities.map(x => x.orders));
                const pct = (c.orders / maxOrders) * 100;
                return (
                  <li key={c.city} className="space-y-1.5" data-testid={`top-city-${idx}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span>{c.city}</span>
                      <span className="text-gold font-serif">{c.orders} <span className="text-muted text-[10px] uppercase tracking-[0.18em] ml-1">orders · {formatPrice(c.revenue)}</span></span>
                    </div>
                    <div className="h-1 bg-[#1a1a1a] overflow-hidden">
                      <div className="h-full bg-gold/80" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* ────────── Visitor funnel (from activity events) ────────── */}
      {events?.funnel && (
        <section className="bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)]" data-testid="visitor-funnel">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <p className="text-xs uppercase tracking-[0.25em] text-gold flex items-center gap-2"><Activity size={12} strokeWidth={1.5} /> Visitor Funnel</p>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted">{events.total_events.toLocaleString()} events tracked · {periodLabel.toLowerCase()}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FunnelCell label="Product views" value={events.funnel.views} sub="Detail pages opened" />
            <FunnelCell label="Add to cart" value={events.funnel.add_to_cart} sub={`${events.funnel.atc_rate}% of views`} />
            <FunnelCell label="Checkout started" value={events.funnel.checkout} sub={`${events.funnel.checkout_rate}% of adds`} />
            <FunnelCell label="Conversion" value={`${events.funnel.conversion_rate}%`} sub="Views → purchase" accent />
          </div>
        </section>
      )}

      {/* ────────── Recent activity ────────── */}
      <section className="bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)]">
        <div className="flex items-center justify-between mb-5">
          <p className="text-xs uppercase tracking-[0.25em] text-gold flex items-center gap-2"><Activity size={12} strokeWidth={1.5} /> Recent activity</p>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted">Last 8 orders</span>
        </div>
        <ul className="divide-y divide-[rgba(244,244,240,0.05)]">
          {data.recent_orders.map(o => (
            <li key={o.order_id} className="py-3 flex items-center gap-4 hover:bg-[#1a1a1a] -mx-2 px-2 transition-colors group" data-testid={`activity-${o.order_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-sm truncate">{o.order_id}</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted mt-0.5 truncate">
                  {o.shipping?.name || o.user_email} · {o.item_count} pieces · {new Date(o.created_at).toLocaleDateString()}
                </p>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.18em] border px-2 py-1 ${STATUS_TONE[o.status] || "text-muted border-[rgba(244,244,240,0.1)]"}`}>{o.status}</span>
              <p className="font-serif text-base text-gold whitespace-nowrap w-24 text-right">{formatPrice(o.total)}</p>
              <button onClick={() => onOpenOrder?.(o.order_id)} className="p-2 text-muted group-hover:text-gold transition-colors" title="View order" data-testid={`activity-view-${o.order_id}`}>
                <Eye size={14} strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "evening";
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

function FunnelCell({ label, value, sub, accent }) {
  return (
    <div className={`p-4 border ${accent ? "border-gold/30 bg-gold/5" : "border-[rgba(244,244,240,0.05)] bg-[#0C0C0C]"}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted">{label}</p>
      <p className={`font-serif text-3xl leading-none mt-2 ${accent ? "text-gold" : "text-text"}`}>{typeof value === "number" ? value.toLocaleString() : value}</p>
      <p className="text-[10px] uppercase tracking-[0.15em] text-muted/70 mt-2">{sub}</p>
    </div>
  );
}
